<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper dq-savings">
    <div class="content">
      <!-- Header -->
      <div class="dq-topbar mb-3">
        <div
          class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2"
        >
          <breadcrumb
            :title="pageTitle"
            :text="pageSection"
            :text1="pageCrumb"
          />

          <div class="d-flex align-items-center gap-2">
            <a
              v-if="canCreateSavings"
              class="btn btn-primary btn-sm d-inline-flex align-items-center"
              @click="openCreate()"
            >
              <i class="ti ti-plus me-1"></i> Add Savings Account
            </a>

            <button
              class="btn dq-btn-ghost btn-sm d-inline-flex align-items-center"
              @click="fetchRows()"
              :disabled="loading"
              title="Refresh"
            >
              <i class="ti ti-refresh me-1"></i>
              <span class="d-none d-md-inline">Refresh</span>
            </button>

            <div class="head-icons ms-1">
              <a id="collapse-header" @click="toggleHeader"
                ><i class="ti ti-chevrons-up"></i
              ></a>
            </div>
          </div>
        </div>

        <!-- Subtext -->
        <div class="dq-subtext mt-2">
          <i class="ti ti-bolt me-1"></i>
          {{ pageSubtext }}
        </div>
      </div>

      <!-- Premium Summary + Filters -->
      <div class="card dq-card mb-3">
        <div class="card-body">
          <div class="row g-3 align-items-end">
            <!-- Summary -->
            <div class="col-12 col-lg-4">
              <div class="dq-summary">
                <div class="dq-summary-title">
                  <i class="ti ti-pig-money me-1"></i> Overview
                </div>

                <div class="dq-metrics mt-2">
                  <div class="dq-metric">
                    <div class="dq-metric-label">Accounts</div>
                    <div class="dq-metric-value">
                      {{ filteredRows.length }}
                    </div>
                  </div>
                  <div class="dq-metric">
                    <div class="dq-metric-label">Total Monthly</div>
                    <div class="dq-metric-value">
                      {{ currency }} {{ money(totalMonthly) }}
                    </div>
                  </div>
                </div>

                <div class="dq-pills mt-2">
                  <span class="badge dq-pill">
                    Active:
                    <strong class="ms-1">{{ countByStatus.active }}</strong>
                  </span>
                  <span class="badge dq-pill">
                    Paused:
                    <strong class="ms-1">{{ countByStatus.paused }}</strong>
                  </span>
                  <span class="badge dq-pill">
                    Closed:
                    <strong class="ms-1">{{ countByStatus.closed }}</strong>
                  </span>
                </div>
              </div>
            </div>

            <!-- Filters -->
            <div class="col-12 col-lg-8">
              <div class="dq-filters">
                <div class="dq-filters-head d-flex align-items-center gap-2">
                  <span class="dq-ic">
                    <i class="ti ti-filter"></i>
                  </span>
                  <div class="dq-filters-title">Filters</div>
                  <div class="ms-auto d-flex gap-2">
                    <button
                      class="btn dq-btn-ghost btn-sm"
                      @click="clearFilters"
                      :disabled="loading"
                    >
                      <i class="ti ti-eraser me-1"></i> Clear
                    </button>
                    <button
                      class="btn dq-btn-outline btn-sm"
                      @click="fetchRows()"
                      :disabled="loading"
                    >
                      <i class="ti ti-search me-1"></i> Apply
                    </button>
                  </div>
                </div>

                <div class="row g-2 mt-2">
                  <div v-if="!isEmployeeSelfService" class="col-12 col-md-4">
                    <label class="form-label dq-label">Period</label>
                    <input
                      v-model="filters.period"
                      class="form-control dq-input"
                      placeholder="YYYY-MM"
                      inputmode="numeric"
                    />
                    <div class="dq-hint">Used for payroll runs (optional).</div>
                  </div>

                  <div v-if="!isEmployeeSelfService" class="col-12 col-md-4">
                    <label class="form-label dq-label">Search</label>
                    <input
                      v-model="filters.search"
                      class="form-control dq-input"
                      placeholder="Employee name / ID"
                    />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label dq-label">Category</label>
                    <select v-model="filters.type" class="form-select dq-select">
                      <option value="">All Categories</option>
                      <option value="general">General Savings</option>
                      <option value="pension">Pension</option>
                      <option value="insurance">Insurance</option>
                    </select>
                  </div>

                  <div class="col-6 col-md-2">
                    <label class="form-label dq-label">Status</label>
                    <select
                      v-model="filters.status"
                      class="form-select dq-select"
                    >
                      <option value="">All</option>
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card dq-card">
        <div class="card-body">
          <div
            class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2"
          >
            <div class="dq-table-title">
              {{ pageTitle }}
              <span class="dq-table-sub">
                {{ filteredRows.length }} item(s)
              </span>
            </div>

            <div
              v-if="!isEmployeeSelfService"
              class="dq-table-actions d-flex align-items-center gap-2"
            >
              <span class="dq-kbd-hint d-none d-md-inline">
                Tip: search by employee ID
              </span>
            </div>
          </div>

          <div class="table-responsive dq-table-wrap">
            <table class="table dq-table mb-0">
              <thead>
                <tr>
                  <th v-if="!isEmployeeSelfService">Employee</th>
                  <th>Category</th>
                  <th>Name</th>
                  <th class="text-end">Monthly Contribution</th>
                  <th>Status</th>
                  <th v-if="showActionsColumn" class="text-end">Actions</th>
                </tr>
              </thead>

              <tbody v-if="loading">
                <tr v-for="n in 6" :key="'sk-' + n" class="dq-skel-row">
                  <td v-if="!isEmployeeSelfService">
                    <div class="dq-skel w-70"></div>
                    <div class="dq-skel w-40 mt-2"></div>
                  </td>
                  <td><div class="dq-skel w-50"></div></td>
                  <td><div class="dq-skel w-80"></div></td>
                  <td class="text-end">
                    <div class="dq-skel w-60 ms-auto"></div>
                  </td>
                  <td><div class="dq-skel w-50"></div></td>
                  <td v-if="showActionsColumn" class="text-end">
                    <div class="dq-skel w-40 ms-auto"></div>
                  </td>
                </tr>
              </tbody>

              <tbody v-else-if="filteredRows.length === 0">
                <tr>
                  <td
                    :colspan="
                      (isEmployeeSelfService ? 0 : 1) +
                      3 +
                      1 +
                      1 +
                      (showActionsColumn ? 1 : 0)
                    "
                    class="py-5 text-center"
                  >
                    <div class="dq-empty">
                      <div class="dq-empty-icon">
                        <i class="ti ti-folder-off"></i>
                      </div>
                      <div class="dq-empty-title">
                        No savings accounts found
                      </div>
                      <div class="dq-empty-sub">
                        Try adjusting filters or add a new savings account.
                      </div>
                      <a
                        v-if="canCreateSavings"
                        class="btn btn-primary btn-sm mt-2"
                        @click="openCreate()"
                      >
                        <i class="ti ti-plus me-1"></i> Add Savings Account
                      </a>
                    </div>
                  </td>
                </tr>
              </tbody>

              <tbody v-else>
                <tr v-for="row in filteredRows" :key="row.id" class="dq-row">
                  <td v-if="!isEmployeeSelfService">
                    <div class="d-flex align-items-center gap-2">
                      <div class="dq-avatar">
                        <img
                          v-if="row.employee.avatar_url"
                          :src="row.employee.avatar_url"
                          alt="avatar"
                        />
                        <span v-else>
                          {{
                            (row.employee.employee_name || "E")
                              .slice(0, 1)
                              .toUpperCase()
                          }}
                        </span>
                      </div>
                      <div class="min-w-0">
                        <div class="fw-semibold text-dark text-truncate">
                          {{ row.employee.employee_name }}
                        </div>
                        <div class="fs-12 text-muted text-truncate">
                          {{ row.employee.employee_id }}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td>
                    <span
                      class="badge dq-badge"
                      :class="typeBadge(row.category)"
                    >
                      {{ prettyType(row.category) }}
                    </span>
                  </td>

                  <td class="text-truncate" style="max-width: 320px">
                    {{ row.name || "—" }}
                  </td>

                  <td class="text-end fw-semibold">
                    {{ currency }} {{ money(row.monthly_contribution) }}
                  </td>

                  <td>
                    <span
                      class="badge dq-badge"
                      :class="statusBadge(row.status)"
                    >
                      {{ row.status || "active" }}
                    </span>
                  </td>

                  <td v-if="showActionsColumn" class="text-end">
                    <div class="d-inline-flex gap-1">
                      <button
                        v-if="canEditSavings"
                        class="btn dq-icon-btn"
                        @click="openEdit(row)"
                        title="Edit"
                      >
                        <i class="ti ti-edit"></i>
                      </button>
                      <button
                        v-if="canDeleteSavings"
                        class="btn dq-icon-btn dq-danger"
                        @click="removeRow(row)"
                        title="Delete"
                      >
                        <i class="ti ti-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Totals Bar -->
          <div class="dq-totals mt-3">
            <div class="text-muted">
              Showing <strong>{{ filteredRows.length }}</strong> item(s)
            </div>
            <div class="dq-totals-right">
              <span class="dq-totals-label">Total Monthly</span>
              <span class="dq-totals-value"
                >{{ currency }} {{ money(totalMonthly) }}</span
              >
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Premium Modal -->
    <div class="modal fade" ref="modalRef" tabindex="-1">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content dq-modal">
          <div class="modal-header dq-modal-header">
            <div class="d-flex align-items-center gap-2">
              <div class="dq-modal-ic">
                <i class="ti ti-pig-money"></i>
              </div>
              <div>
                <div class="dq-modal-title">
                  {{ form.id ? "Edit Savings Account" : "Add Savings Account" }}
                </div>
                <div class="dq-modal-sub">
                  Configure employee savings & payroll deduction behavior.
                </div>
              </div>
            </div>

            <button
              type="button"
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
            ></button>
          </div>

          <div class="modal-body">
            <div class="dq-section">
              <div class="dq-section-title">
                <i class="ti ti-user me-1"></i> Employee & Payroll
              </div>

              <div class="row g-3 mt-1">
                <div class="col-md-7">
                  <EmployeeSelectDropdown
                    v-model="form.employee_id"
                    :employees="employeeDropdownEmployees"
                    mode="single"
                    label="Employee"
                    help="Select employee whose savings contribution you’re managing."
                    :disabled="saving"
                  />
                </div>

                <div class="col-md-5">
                  <label class="form-label dq-label">Deduct in Payroll?</label>
                  <select
                    v-model="form.deduct_in_payroll"
                    class="form-select dq-select"
                    :disabled="saving"
                  >
                    <option :value="true">Yes</option>
                    <option :value="false">No</option>
                  </select>
                  <div class="dq-hint">
                    If “Yes”, payroll will apply this automatically.
                  </div>
                </div>
              </div>
            </div>

            <div class="dq-divider"></div>

            <div class="dq-section">
              <div class="dq-section-title">
                <i class="ti ti-category me-1"></i> Savings Details
              </div>

              <div class="row g-3 mt-1">
                <div class="col-md-4">
                  <label class="form-label dq-label">Category</label>
                  <select
                    v-model="form.category"
                    class="form-select dq-select"
                    :disabled="saving || isEmployeeSelfService"
                  >
                    <option value="general">General Savings</option>
                    <option value="pension">Pension</option>
                    <option value="insurance">Insurance</option>
                  </select>
                </div>

                <div class="col-md-8">
                  <label class="form-label dq-label">Name</label>
                  <input
                    v-model="form.name"
                    class="form-control dq-control"
                    placeholder=""
                    :disabled="saving"
                    required
                  />
                </div>

                <div class="col-md-4">
                  <label class="form-label dq-label">Start Period</label>
                  <input
                    v-model="form.start_period"
                    class="form-control dq-control"
                    placeholder="YYYY-MM"
                    type="date"
                  />
                  <div class="dq-hint">
                    First payroll month to start deducting.
                  </div>
                </div>

                <div class="col-md-4">
                  <label class="form-label dq-label"
                    >End Period (optional)</label
                  >
                  <input
                    v-model="form.end_period"
                    class="form-control dq-control"
                    placeholder="YYYY-MM"
                    type="date"
                  />
                  <div class="dq-hint">Stop deductions after this month.</div>
                </div>

                <div class="col-md-4">
                  <label class="form-label dq-label"
                    >Monthly Contribution</label
                  >
                  <div class="dq-money-input">
                    <span class="dq-money-prefix">{{ currency }}</span>
                    <input
                      v-model.number="form.monthly_contribution"
                      type="number"
                      class="form-control dq-control"
                      min="0"
                      :disabled="saving"
                    />
                  </div>
                  <div class="dq-hint">
                    Amount deducted per month (if enabled).
                  </div>
                </div>

                <div class="col-md-4">
                  <label class="form-label dq-label">Status</label>
                  <select
                    v-model="form.status"
                    class="form-select dq-select"
                    :disabled="saving"
                  >
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                <div class="col-md-4">
                  <label class="form-label dq-label">Preview</label>
                  <div class="dq-preview">
                    <div class="dq-preview-top">
                      <span
                        class="badge dq-badge"
                        :class="typeBadge(form.category)"
                      >
                        {{ prettyType(form.category) }}
                      </span>
                      <span
                        class="badge dq-badge"
                        :class="statusBadge(form.status)"
                      >
                        {{ form.status }}
                      </span>
                    </div>
                    <div class="dq-preview-name">{{ form.name || "—" }}</div>
                    <div class="dq-preview-amt">
                      {{ currency }} {{ money(form.monthly_contribution) }} /
                      month
                    </div>
                  </div>
                </div>

                <div class="col-12">
                  <div class="dq-callout">
                    <div class="dq-callout-title">
                      <i class="ti ti-info-circle me-1"></i> Payroll integration
                    </div>
                    <div class="dq-callout-sub">
                      These savings auto-apply during payroll runs for matching
                      periods. Pension & Insurance may affect PAYE reliefs/caps
                      in your calculator.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-modal-footer">
            <a
              class="btn dq-btn-ghost"
              data-bs-dismiss="modal"
              :disabled="saving"
            >
              Cancel
            </a>
            <a
              class="btn btn-primary"
              @click="save"
              :disabled="saving || !Number(form.employee_id)"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              <i v-else class="ti ti-device-floppy me-3"></i>
              {{ saving ? "Saving..." : "Save" }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p class="mb-0">
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { Modal } from "bootstrap";
import moment from "moment";
import { storeToRefs } from "pinia";
import { useSavingsStore } from "@/stores/savingsStore";
import EmployeeSelectDropdown from "@/components/common/EmployeeSelectDropdown.vue";
import { useEmployeeStore } from "@/stores/employeeStore";
import { useCompanyStore } from "@/stores/companyStore";
import { useAuthStore } from "@/stores/authStore";

const store = useSavingsStore();
const companyStore = useCompanyStore();
const employeeStore = useEmployeeStore();
const authStore = useAuthStore();

const modalRef = ref(null);
let modal;

const { companies, activeCompanyId } = storeToRefs(companyStore);
const { user } = storeToRefs(authStore);

const activeCompany = computed(() =>
  (companies.value || []).find((c) => c.id === activeCompanyId.value),
);

const currency = computed(() => activeCompany.value?.currency_code || "KES");
const currentEmployeeId = computed(
  () => Number(user.value?.employee?.id || 0) || null,
);
const isEmployeeSelfService = computed(() => {
  const roles = (user.value?.roles || []).map((r) =>
    typeof r === "string" ? r : r?.name,
  );
  return roles.includes("Employee") && !!currentEmployeeId.value;
});
const canReadSavings = computed(
  () => authStore.can("Savings.Read") || isEmployeeSelfService.value,
);
const canCreateSavings = computed(() => authStore.can("Savings.Create"));
const canEditSavings = computed(() => authStore.can("Savings.Edit"));
const canDeleteSavings = computed(() => authStore.can("Savings.Delete"));
const showActionsColumn = computed(
  () => canEditSavings.value || canDeleteSavings.value,
);
const pageTitle = computed(() =>
  isEmployeeSelfService.value ? "My Savings" : "Savings Accounts",
);
const pageSection = computed(() =>
  isEmployeeSelfService.value ? "Employee" : "HR",
);
const pageCrumb = computed(() =>
  isEmployeeSelfService.value ? "My Savings" : "Savings",
);
const pageSubtext = computed(() =>
  isEmployeeSelfService.value
    ? "Your payroll-linked savings contributions are shown here."
    : "Savings here feed payroll automatically.",
);

const filters = reactive({
  period: moment().format("YYYY-MM"),
  search: "",
  type: "",
  status: "",
});

const form = reactive({
  id: null,
  employee_id: "",
  savings_type_id: null,
  monthly_contribution: 0,
  deduct_in_payroll: true,
  period: moment().format("YYYY-MM"),
  category: "general",
  name: "",
  status: "active",
  start_period: moment().format("YYYY-MM"),
  end_period: "",
});

// Some stores expose store.saving / store.loading. Keep resilient.
const loading = computed(() => !!store.loading);
const saving = computed(() => !!store.saving);

const rows = computed(() => store.rows || []);

const employeeDropdownEmployees = computed(() =>
  (employeeStore.employees || []).map((e) => ({
    id: e.id,
    name: `${e.first_name || ""} ${e.last_name || ""}`.trim() || e.name || "—",
    employee_id: e.employee_id || e.employee_no || e.staff_no || "",
    avatar_url: e.avatar_url || e.photo_url || null,
    department: e.department?.name || e.department_name || "",
    designation: e.designation?.name || e.designation_name || "",
  })),
);

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function money(v) {
  return Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
}

const filteredRows = computed(() => {
  const q = (filters.search || "").toLowerCase();

  return (rows.value || [])
    .filter((r) => (filters.type ? r.category === filters.type : true))
    .filter((r) =>
      filters.status ? (r.status || "active") === filters.status : true,
    )
    .filter((r) => {
      if (!q) return true;
      return (
        (r.employee_name || "").toLowerCase().includes(q) ||
        String(r.employee_id || "")
          .toLowerCase()
          .includes(q)
      );
    });
});

const totalMonthly = computed(() =>
  filteredRows.value.reduce(
    (sum, r) => sum + Number(r.monthly_contribution || 0),
    0,
  ),
);

const countByStatus = computed(() => {
  const base = { active: 0, paused: 0, closed: 0 };
  for (const r of filteredRows.value) {
    const s = (r.status || "active").toLowerCase();
    if (s in base) base[s] += 1;
  }
  return base;
});

function typeBadge(type) {
  if (type === "pension") return "dq-success";
  if (type === "insurance") return "dq-warning";
  return "dq-primary";
}

function statusBadge(status) {
  if (status === "closed") return "dq-danger";
  if (status === "paused") return "dq-warning";
  return "dq-success";
}

function prettyType(type) {
  if (type === "pension") return "Pension";
  if (type === "insurance") return "Insurance";
  return "General Savings";
}

async function fetchRows() {
  if (!canReadSavings.value) {
    store.rows = [];
    return;
  }

  await store.fetch({
    status: filters.status || undefined,
    category: filters.type || undefined,
    search: filters.search || undefined,
    ...(isEmployeeSelfService.value && currentEmployeeId.value
      ? { employee_id: currentEmployeeId.value }
      : {}),
  });
}

function clearFilters() {
  filters.period = moment().format("YYYY-MM");
  filters.search = "";
  filters.type = "";
  filters.status = "";
  fetchRows();
}

function resetForm() {
  form.id = null;
  form.employee_id = "";
  form.savings_type_id = null;
  form.category = "general";
  form.name = "";
  form.monthly_contribution = 0;
  form.deduct_in_payroll = true;
  form.status = "active";
}

function openCreate() {
  if (!canCreateSavings.value) return;
  resetForm();
  if (isEmployeeSelfService.value && currentEmployeeId.value) {
    form.employee_id = currentEmployeeId.value;
  }
  modal?.show();
}

function openEdit(row) {
  if (!canEditSavings.value) return;
  form.id = row.id;
  form.employee_id = row.employee_id;
  form.savings_type_id = row.savings_type_id || null;
  form.category = row.category || "general";
  form.name = row.name || "";
  form.monthly_contribution = Number(row.monthly_contribution || 0);
  form.deduct_in_payroll = !!row.deduct_in_payroll;
  form.status = row.status || "active";
  form.start_period = row.start_period || null;
  form.end_period = row.end_period || null;

  modal?.show();
}

async function save() {
  if (!canCreateSavings.value && !(form.id && canEditSavings.value)) return;

  const payload = {
    employee_id: Number(form.employee_id),
    savings_type_id: form.savings_type_id ? Number(form.savings_type_id) : null,
    name: form.name,
    category: form.category,

    monthly_contribution: Number(form.monthly_contribution || 0),
    deduct_in_payroll: !!form.deduct_in_payroll,
    start_period: form.start_period || null,
    end_period: form.end_period || null,
    status: form.status,
  };

  if (!payload.employee_id) return;

  if (form.id) await store.update(form.id, payload);
  else await store.create(payload);

  modal?.hide();
  await fetchRows();
}

async function removeRow(row) {
  if (!canDeleteSavings.value) return;
  const ok = confirm("Remove this savings item?");
  if (!ok) return;

  await store.destroy(row.id);
  await fetchRows();
}

onMounted(async () => {
  modal = new Modal(modalRef.value);
  if (canCreateSavings.value || canEditSavings.value) {
    await employeeStore.fetchEmployees(
      isEmployeeSelfService.value && currentEmployeeId.value
        ? { employee_id: currentEmployeeId.value, per_page: 5000 }
        : { per_page: 5000 },
    );
  }
  await fetchRows();
});
</script>

<style scoped>
/* ========= Premium Surface ========= */
.dq-savings {
  --dq-border: rgba(15, 23, 42, 0.1);
  --dq-soft: rgba(15, 23, 42, 0.06);
  --dq-text: #0f172a;
  --dq-muted: rgba(15, 23, 42, 0.65);
  --dq-muted2: rgba(15, 23, 42, 0.45);
  --dq-white: #ffffff;
  --dq-shadow: 0 14px 35px rgba(2, 6, 23, 0.08);
  --dq-shadow2: 0 10px 24px rgba(2, 6, 23, 0.08);
  --dq-radius: 18px;
}

.dq-topbar {
  padding: 14px 14px 0 14px;
}
.dq-subtext {
  font-size: 12px;
  color: var(--dq-muted);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid var(--dq-soft);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(8px);
}

.dq-card {
  border: 1px solid var(--dq-border) !important;
  border-radius: var(--dq-radius) !important;
  box-shadow: var(--dq-shadow2);
  overflow: hidden;
}

.dq-btn-primary {
  background: #0f172a !important;
  border-color: #0f172a !important;
  color: #fff !important;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.18);
}
.dq-btn-primary:hover {
  transform: translateY(-1px);
}
.dq-btn-ghost {
  background: rgba(15, 23, 42, 0.06) !important;
  border: 1px solid rgba(15, 23, 42, 0.1) !important;
  color: #0f172a !important;
}
.dq-btn-outline {
  background: #fff !important;
  border: 1px solid rgba(15, 23, 42, 0.14) !important;
  color: #0f172a !important;
}

/* ========= Summary ========= */
.dq-summary {
  border: 1px solid var(--dq-soft);
  border-radius: 16px;
  padding: 14px;
  background: radial-gradient(
      900px 220px at 10% 0%,
      rgba(15, 23, 42, 0.06),
      transparent 55%
    ),
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.92),
      rgba(255, 255, 255, 0.85)
    );
}
.dq-summary-title {
  font-weight: 800;
  color: var(--dq-text);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dq-metrics {
  display: flex;
  gap: 10px;
}
.dq-metric {
  flex: 1;
  border: 1px solid var(--dq-soft);
  background: rgba(255, 255, 255, 0.9);
  border-radius: 14px;
  padding: 10px;
}
.dq-metric-label {
  font-size: 12px;
  color: var(--dq-muted2);
}
.dq-metric-value {
  font-size: 16px;
  font-weight: 900;
  color: var(--dq-text);
  margin-top: 2px;
}
.dq-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.dq-pill {
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.05);
  color: var(--dq-text);
  border-radius: 999px;
  padding: 7px 10px;
  font-weight: 600;
}

/* ========= Filters ========= */
.dq-filters {
  border: 1px solid var(--dq-soft);
  border-radius: 16px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.92);
}
.dq-filters-head {
  padding-bottom: 10px;
  border-bottom: 1px dashed rgba(15, 23, 42, 0.14);
}
.dq-ic {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(15, 23, 42, 0.1);
  color: #0f172a;
}
.dq-filters-title {
  font-weight: 800;
  color: var(--dq-text);
}
.dq-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--dq-muted);
}
.dq-hint {
  font-size: 11px;
  color: var(--dq-muted2);
  margin-top: 6px;
}
.dq-input .form-control,
.dq-select,
.dq-control {
  border-radius: 12px !important;
  border: 1px solid rgba(15, 23, 42, 0.14) !important;
  box-shadow: none !important;
}
.dq-input .input-icon-addon {
  color: rgba(15, 23, 42, 0.6);
}

/* ========= Table ========= */
.dq-table-title {
  font-weight: 900;
  color: var(--dq-text);
}
.dq-table-sub {
  font-weight: 700;
  color: var(--dq-muted2);
  font-size: 12px;
  margin-left: 8px;
}
.dq-table-wrap {
  border: 1px solid var(--dq-soft);
  border-radius: 16px;
  overflow: hidden;
}
.dq-table {
  margin: 0;
}
.dq-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: linear-gradient(180deg, #ffffff, #fbfbfc);
  border-bottom: 1px solid rgba(15, 23, 42, 0.12) !important;
  color: rgba(15, 23, 42, 0.75);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.dq-row {
  transition: transform 0.15s ease, background 0.15s ease;
}
.dq-row:hover {
  background: rgba(15, 23, 42, 0.03);
}
.dq-avatar {
  width: 36px;
  height: 36px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: rgba(15, 23, 42, 0.06);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: #0f172a;
  flex: 0 0 auto;
}
.dq-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.dq-badge {
  border-radius: 999px !important;
  padding: 7px 10px !important;
  font-weight: 800 !important;
  border: 1px solid rgba(15, 23, 42, 0.12) !important;
}
.dq-primary {
  background: rgba(59, 130, 246, 0.1) !important;
  color: #1d4ed8 !important;
}
.dq-success {
  background: rgba(16, 185, 129, 0.12) !important;
  color: #047857 !important;
}
.dq-warning {
  background: rgba(245, 158, 11, 0.14) !important;
  color: #92400e !important;
}
.dq-danger {
  background: rgba(239, 68, 68, 0.12) !important;
  color: #b91c1c !important;
}

.dq-icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.05);
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.dq-icon-btn:hover {
  background: rgba(15, 23, 42, 0.08);
}
.dq-icon-btn.dq-danger {
  border-color: rgba(239, 68, 68, 0.22);
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
}

.dq-totals {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border: 1px solid var(--dq-soft);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
}
.dq-totals-right {
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
}
.dq-totals-label {
  font-size: 12px;
  color: var(--dq-muted2);
  font-weight: 700;
}
.dq-totals-value {
  font-weight: 900;
  color: var(--dq-text);
}

/* ========= Skeleton ========= */
.dq-skel-row td {
  padding-top: 14px;
  padding-bottom: 14px;
}
.dq-skel {
  height: 12px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(15, 23, 42, 0.06),
    rgba(15, 23, 42, 0.1),
    rgba(15, 23, 42, 0.06)
  );
  background-size: 200% 100%;
  animation: dqShimmer 1.1s infinite linear;
}
@keyframes dqShimmer {
  0% {
    background-position: 0% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
.w-40 {
  width: 40%;
}
.w-50 {
  width: 50%;
}
.w-60 {
  width: 60%;
}
.w-70 {
  width: 70%;
}
.w-80 {
  width: 80%;
}

/* ========= Empty State ========= */
.dq-empty {
  display: grid;
  place-items: center;
  gap: 6px;
}
.dq-empty-icon {
  width: 56px;
  height: 56px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  background: rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(15, 23, 42, 0.1);
  font-size: 22px;
}
.dq-empty-title {
  font-weight: 900;
  color: var(--dq-text);
}
.dq-empty-sub {
  font-size: 12px;
  color: var(--dq-muted);
  max-width: 360px;
}

/* ========= Modal ========= */
.dq-modal {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.14);
  box-shadow: var(--dq-shadow);
}
.dq-modal-header {
  border: 0;
  color: #fff;
  padding: 16px 18px;
  background: radial-gradient(
      900px 260px at 10% 0%,
      rgba(255, 255, 255, 0.2),
      transparent 55%
    ),
    linear-gradient(135deg, #0f172a, #111827);
}
.dq-modal-ic {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.2);
}
.dq-modal-title {
  font-weight: 900;
  line-height: 1.1;
}
.dq-modal-sub {
  font-size: 12px;
  opacity: 0.85;
}
.dq-section-title {
  font-weight: 900;
  color: var(--dq-text);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dq-divider {
  height: 1px;
  background: rgba(15, 23, 42, 0.1);
  margin: 16px 0;
}
.dq-callout {
  border-radius: 16px;
  border: 1px solid rgba(59, 130, 246, 0.18);
  background: rgba(59, 130, 246, 0.08);
  padding: 12px 14px;
}
.dq-callout-title {
  font-weight: 900;
  color: #0f172a;
}
.dq-callout-sub {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.75);
}

.dq-money-input {
  position: relative;
}
.dq-money-prefix {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.7);
  padding: 4px 8px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(15, 23, 42, 0.1);
}
.dq-money-input input {
  padding-left: 78px !important;
}

.dq-preview {
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.03);
  border-radius: 16px;
  padding: 12px;
}
.dq-preview-top {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.dq-preview-name {
  font-weight: 900;
  margin-top: 8px;
  color: var(--dq-text);
}
.dq-preview-amt {
  font-size: 12px;
  color: var(--dq-muted);
  margin-top: 4px;
}

.dq-modal-footer {
  border-top: 1px solid rgba(15, 23, 42, 0.1);
  background: rgba(255, 255, 255, 0.9);
  gap: 10px;
}
</style>


